import type { City, DoctorService, DoctorServiceExpanded } from "@hermes/api";
import { Temporal } from "@hermes/intl";
import { availableCountries, resolveCountryInfo } from "@hermes/shared/schemas";
import useLocalStorage from "@rehooks/local-storage";
import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { useEffect } from "react";

export const getCityById = (
  cities: City[],
  cityId?: number,
): City | undefined => {
  return cityId ? cities.find((c) => c.id === cityId) : undefined;
};

export const sortServicesByName = (
  services?: DoctorService[],
): DoctorService[] | undefined => {
  return services?.toSorted((a, b) =>
    (a.service_name?.toLowerCase() ?? "").localeCompare(
      b.service_name?.toLowerCase() ?? "",
    ),
  );
};

export const countryInfo = resolveCountryInfo(
  availableCountries.parse(import.meta.env.DOQ_COUNTRY_ID),
);

export const useStoredLocale = () => {
  return useLocalStorage<string>("locale", countryInfo.defaultLocale);
};

declare global {
  interface Window {
    prerenderReady?: boolean;
  }
}

export function usePrerenderReady(delay = 200) {
  const isFetching = useIsFetching();
  const isMutating = useIsMutating();

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const checkReady = () => {
      if (isFetching === 0 && isMutating === 0) {
        // Small delay to ensure Helmet and Emotion finish
        timeout = setTimeout(() => {
          window.prerenderReady = true;
        }, delay);
      } else {
        // Still loading – reset if needed
        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }
      }
    };

    checkReady(); // initial check

    const interval = setInterval(checkReady, 50); // re-check every 50ms

    return () => {
      clearInterval(interval);
      if (timeout) clearTimeout(timeout);
    };
  }, [isFetching, isMutating, delay]);
}

export const sortDoctorServiceByFeedback = <
  DoctorServiceLike extends Pick<
    DoctorService,
    "feedback_count" | "feedback_score" | "nearest_slot_datetime"
  >,
>(
  a: DoctorServiceLike,
  b: DoctorServiceLike,
) => {
  if (b.feedback_count !== a.feedback_count) {
    return b.feedback_count - a.feedback_count;
  }
  // Leave at the same position, services without slots will be filtered out later
  if (!a.nearest_slot_datetime || !b.nearest_slot_datetime) {
    return 0;
  }

  return Temporal.PlainDateTime.compare(
    a.nearest_slot_datetime,
    b.nearest_slot_datetime,
  );
};

/**
 * Selects the most relevant doctor service.
 *
 * 1. Consider only services with available appointment slots
 * 2. Among them, prefer those with the highest number of feedbacks
 * 3. If equal, prefer those with the earliest available slot
 */
export const selectMainDoctorService = <
  DoctorServiceLike extends Pick<
    DoctorService | DoctorServiceExpanded,
    | "feedback_count"
    | "feedback_score"
    | "nearest_slot_datetime"
    | "clinic_branch"
    | "is_premium"
    | "service"
  >,
>(
  doctorServices: DoctorServiceLike[],
  filters?: {
    clinic_branch?: number;
    is_premium?: boolean;
    service?: number;
  },
): DoctorServiceLike | undefined => {
  const applyFilters = (dService: DoctorServiceLike) => {
    const serviceId =
      typeof dService.service === "number"
        ? dService.service
        : dService.service.id;
    return (
      (filters?.clinic_branch
        ? dService.clinic_branch === filters.clinic_branch
        : true) &&
      (typeof filters?.is_premium !== "undefined"
        ? filters.is_premium === dService.is_premium
        : true) &&
      (typeof filters?.service !== "undefined"
        ? filters.service === serviceId
        : true)
    );
  };

  const sortedByFeedback = doctorServices
    .sort(sortDoctorServiceByFeedback)
    .filter(applyFilters);

  // Consider only with slots
  const filteredBySlots = sortedByFeedback.filter(
    (service) => service.nearest_slot_datetime,
  );

  // Fallback to first sorted
  return (
    filteredBySlots[0] ??
    sortedByFeedback[0] ??
    doctorServices.filter(applyFilters)[0]
  );
};
